/**
 * Locale-aware relative-time helper. Wraps Intl.RelativeTimeFormat (built into
 * every modern browser; no extra bundle weight) and picks the most appropriate
 * unit (second / minute / hour / day / month / year) based on the elapsed time.
 *
 * Why not moment.fromNow():
 *   - moment's locale needs to be globally loaded (e.g. `import 'moment/locale/sq'`)
 *     for non-English to work. The app today loads NO moment locales, so every
 *     call to `moment(x).locale('sq').fromNow()` silently returns English.
 *   - Intl.RelativeTimeFormat ships with every browser engine, knows all 7
 *     locales the app supports, and handles plural rules correctly out of the
 *     box. Zero extra bundle code.
 *
 * Usage:
 *   const intl = useIntl();
 *   const text = formatRelativeTime(intl.locale, '2026-05-20T10:00:00Z');
 *   // → "5 days ago" (en), "para 5 ditësh" (sq), "vor 5 Tagen" (de) …
 *
 * The unit chosen mirrors what moment.fromNow does: smallest unit that produces
 * a value ≥ 1. `numeric: 'auto'` so "now", "yesterday", "next week" render as
 * natural phrases in each locale where the CLDR defines them.
 */
export function formatRelativeTime(locale: string, isoDate: string | Date | null | undefined): string {
    if (!isoDate) return '';

    const date = isoDate instanceof Date ? isoDate : new Date(isoDate);
    if (Number.isNaN(date.getTime())) return '';

    const diffMs = date.getTime() - Date.now();
    const absSec = Math.abs(diffMs / 1000);

    // Pick the unit + its scaling divisor. Order matches CLDR's "best unit"
    // convention used by Intl.RelativeTimeFormat.
    let unit: Intl.RelativeTimeFormatUnit;
    let value: number;

    if (absSec < 60) {
        unit = 'second';
        value = Math.round(diffMs / 1000);
    } else if (absSec < 3600) {
        unit = 'minute';
        value = Math.round(diffMs / 60_000);
    } else if (absSec < 86_400) {
        unit = 'hour';
        value = Math.round(diffMs / 3_600_000);
    } else if (absSec < 2_592_000) {
        unit = 'day';
        value = Math.round(diffMs / 86_400_000);
    } else if (absSec < 31_536_000) {
        unit = 'month';
        value = Math.round(diffMs / 2_592_000_000);
    } else {
        unit = 'year';
        value = Math.round(diffMs / 31_536_000_000);
    }

    // Some older browsers don't accept odd BCP-47 tags. Fallback to 'en' on
    // formatter construction failure — never crashes the render.
    try {
        const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
        return rtf.format(value, unit);
    } catch {
        const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
        return rtf.format(value, unit);
    }
}
